import { Box, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const SectionContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  overflow: "hidden",
  width: "100%",
  borderBottom: "1px solid var(--button-border)",
  [theme.breakpoints.down("md")]: {
    gap: "20px",
  },
}));

const Tab = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px 0",
  position: "relative",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.8,
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px 0",
  },
}));

const TabText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  fontSize: "var(--paragraph-p3-font-size)",
  fontWeight: "var(--paragraph-p3-medium-font-weight)",
  lineHeight: "var(--paragraph-p3-line-height)",
  letterSpacing: "var(--paragraph-p3-letter-spacing)",
  fontFamily: "var(--font-family)",
  color: isActive ? "var(--brand-primary)" : "var(--text-secondary)",
  whiteSpace: "nowrap",
}));

const TabLine = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "4px",
  backgroundColor: "var(--brand-primary)",
});

const ContentSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "60px 0",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    gap: "20px",
    padding: "40px 0",
  },
}));

const TitleSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const TextContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  [theme.breakpoints.down("md")]: {
    fontSize: "var(--heading-h2-mobile-font-size)",
    lineHeight: "var(--heading-h2-mobile-line-height)",
  },
}));

const SectionDescription = styled(Typography)({
  color: "var(--text-secondary)",
});

const ImageSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "718px",
  height: "424px",
  backgroundColor: "var(--background-tertiary)",
  borderRadius: "32px",
  overflow: "hidden",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "336px",
    height: "235px",
    borderRadius: "24px",
  },
}));

const SectionImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "center",
});

const CardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "16px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "12px",
  },
}));

const Card = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  [theme.breakpoints.down("md")]: {
    gap: "20px",
  },
}));

const CardImage = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "229px",
  backgroundColor: "var(--background-tertiary)",
  borderRadius: "32px",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    height: "180px",
    borderRadius: "24px",
  },
}));

const CardImageContent = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
});

const CardTextContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const CardTitle = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  [theme.breakpoints.down("md")]: {
    fontSize: "var(--heading-h4-mobile-font-size)",
    lineHeight: "var(--heading-h4-mobile-line-height)",
  },
}));

const CardDescription = styled(Typography)({
  color: "var(--text-secondary)",
});

interface TabItem {
  id: string;
  labelKey: string;
}

interface BusinessCard {
  image: string;
  titleKey: string;
  descriptionKey: string;
}

const tabItems: TabItem[] = [
  { id: "applicants", labelKey: "businessSection.tabIndividuals" },
  { id: "business", labelKey: "businessSection.tabBusiness" },
];

const cardKeys: BusinessCard[] = [
  {
    image: "/assets/profile-1.webp",
    titleKey: "businessSection.card1Title",
    descriptionKey: "businessSection.card1Description",
  },
  {
    image: "/assets/profile-2.webp",
    titleKey: "businessSection.card2Title",
    descriptionKey: "businessSection.card2Description",
  },
  {
    image: "/assets/profile-3.webp",
    titleKey: "businessSection.card3Title",
    descriptionKey: "businessSection.card3Description",
  },
];

export const BusinessSection = () => {
  const [activeTab, setActiveTab] = useState("business");
  const { t } = useTranslation();

  return (
    <SectionContainer>
      <TabsContainer>
        {tabItems.map((tab) => (
          <Tab
            key={tab.id}
            isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <TabText isActive={activeTab === tab.id}>{t(tab.labelKey)}</TabText>
            {activeTab === tab.id && <TabLine />}
          </Tab>
        ))}
      </TabsContainer>

      <ContentSection>
        <TitleSection>
          <TextContent>
            <SectionTitle variant="h2">
              {t("businessSection.title")}
            </SectionTitle>
            <SectionDescription variant="body2">
              {t("businessSection.description")}
            </SectionDescription>
          </TextContent>
        </TitleSection>
        <ImageSection>
          <SectionImage src="/assets/business-analytics.webp" alt={t("images.businessAnalyticsAlt")} />
        </ImageSection>
      </ContentSection>

      <CardsContainer>
        {cardKeys.map((card, index) => (
          <Card key={index}>
            <CardImage>
              <CardImageContent src={card.image} alt={t(card.titleKey)} />
            </CardImage>
            <CardTextContent>
              <CardTitle variant="h4">{t(card.titleKey)}</CardTitle>
              <CardDescription variant="body2">{t(card.descriptionKey)}</CardDescription>
            </CardTextContent>
          </Card>
        ))}
      </CardsContainer>
    </SectionContainer>
  );
};